import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type PointerEvent,
  type RefObject,
} from 'react';
import type { PlanPoint, Size } from '@oscom/shared';
import {
  DRAG_THRESHOLD_PX,
  fitViewport,
  panBy,
  scaleLimits,
  wheelFactor,
  zoomAt,
  type ScaleLimits,
  type Viewport,
} from './viewport';

/** Elements that handle their own pointer input (popover, form controls). */
const OVERLAY_SELECTOR = '[data-plan-overlay]';
/** Interactive children: pressing them must not capture the pointer up front. */
const INTERACTIVE_SELECTOR = 'button, a, input, select, textarea, [data-plan-overlay]';
const KEY_ZOOM_FACTOR = 1.25;

function closestTo(target: EventTarget | null, selector: string): Element | null {
  return target instanceof Element ? target.closest(selector) : null;
}

/** `setPointerCapture` throws for pointers the browser does not know (synthetic events). */
export function capture(element: Element, pointerId: number): void {
  try {
    if (!element.hasPointerCapture?.(pointerId)) element.setPointerCapture?.(pointerId);
  } catch {
    // Not capturable: the gesture still works while the pointer stays inside.
  }
}

export interface PanZoom {
  view: Viewport;
  /** Measures the container now and fits the whole image into it. */
  fit: () => void;
  /** Client coordinates -> container coordinates. */
  toLocal: (clientX: number, clientY: number) => PlanPoint;
  /** True when the current/last gesture travelled more than DRAG_THRESHOLD_PX. */
  wasDrag: () => boolean;
  handlers: {
    onPointerDown: (event: PointerEvent<HTMLDivElement>) => void;
    onPointerMove: (event: PointerEvent<HTMLDivElement>) => void;
    onPointerUp: (event: PointerEvent<HTMLDivElement>) => void;
    onPointerCancel: (event: PointerEvent<HTMLDivElement>) => void;
    onPointerLeave: (event: PointerEvent<HTMLDivElement>) => void;
    onLostPointerCapture: (event: PointerEvent<HTMLDivElement>) => void;
    onKeyDown: (event: KeyboardEvent<HTMLDivElement>) => void;
  };
}

/**
 * Pan, wheel zoom, pinch and keyboard zoom for an image of natural size `image`
 * shown in the container behind `containerRef`. No library: the view is a
 * plain scale + offset (see viewport.ts) applied as a CSS transform.
 */
export function usePanZoom(containerRef: RefObject<HTMLDivElement | null>, image: Size): PanZoom {
  const [view, setView] = useState<Viewport>({ scale: 1, x: 0, y: 0 });
  const limits = useRef<ScaleLimits>(scaleLimits(1));
  const pointers = useRef(new Map<number, PlanPoint>());
  const gesture = useRef<{ moved: number }>({ moved: 0 });
  /** The user panned or zoomed since the last fit: a resize must not undo that. */
  const adjusted = useRef(false);
  /** Container size at the last fit or resize, to tell real resizes apart. */
  const measured = useRef<Size>({ width: 0, height: 0 });

  const toLocal = useCallback(
    (clientX: number, clientY: number): PlanPoint => {
      const rect = containerRef.current?.getBoundingClientRect();
      return { x: clientX - (rect?.left ?? 0), y: clientY - (rect?.top ?? 0) };
    },
    [containerRef],
  );

  const fit = useCallback(() => {
    const rect = containerRef.current?.getBoundingClientRect();
    const size = { width: rect?.width ?? 0, height: rect?.height ?? 0 };
    const next = fitViewport(size, { width: image.width, height: image.height });
    measured.current = size;
    adjusted.current = false;
    limits.current = scaleLimits(next.scale);
    setView(next);
  }, [containerRef, image.width, image.height]);

  // First fit before paint, and again whenever the image size changes.
  useLayoutEffect(() => {
    fit();
  }, [fit]);

  // A resized container triggers a refit (the size is measured in `fit`), but
  // only while the user has not panned or zoomed yet, and only when the size
  // really changed: tablets resize on every URL bar or keyboard toggle, which
  // must not reset the zoom (and close the popover).
  useEffect(() => {
    const element = containerRef.current;
    if (!element || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => {
      const rect = element.getBoundingClientRect();
      const changed =
        rect.width !== measured.current.width || rect.height !== measured.current.height;
      measured.current = { width: rect.width, height: rect.height };
      if (changed && !adjusted.current) fit();
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [containerRef, fit]);

  // React registers wheel listeners as passive; preventDefault needs our own.
  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;
    const onWheel = (event: WheelEvent): void => {
      if (closestTo(event.target, OVERLAY_SELECTOR)) return;
      event.preventDefault();
      const anchor = toLocal(event.clientX, event.clientY);
      adjusted.current = true;
      setView((current) => zoomAt(current, wheelFactor(event.deltaY), anchor, limits.current));
    };
    element.addEventListener('wheel', onWheel, { passive: false });
    return () => element.removeEventListener('wheel', onWheel);
  }, [containerRef, toLocal]);

  const zoomAtCentre = useCallback(
    (factor: number) => {
      const rect = containerRef.current?.getBoundingClientRect();
      const centre = { x: (rect?.width ?? 0) / 2, y: (rect?.height ?? 0) / 2 };
      adjusted.current = true;
      setView((current) => zoomAt(current, factor, centre, limits.current));
    },
    [containerRef],
  );

  function onPointerDown(event: PointerEvent<HTMLDivElement>): void {
    if (closestTo(event.target, OVERLAY_SELECTOR)) return;
    // Capturing right away would retarget the click away from a marker button;
    // there the capture waits until the press turns into a drag.
    if (!closestTo(event.target, INTERACTIVE_SELECTOR)) {
      capture(event.currentTarget, event.pointerId);
    }
    pointers.current.set(event.pointerId, toLocal(event.clientX, event.clientY));
    if (pointers.current.size === 1) gesture.current.moved = 0;
  }

  function onPointerMove(event: PointerEvent<HTMLDivElement>): void {
    const previous = pointers.current.get(event.pointerId);
    if (!previous) return;
    // A mouse released outside the window never sent its pointerup.
    if (event.pointerType === 'mouse' && event.buttons === 0) {
      pointers.current.delete(event.pointerId);
      return;
    }
    const current = toLocal(event.clientX, event.clientY);
    if (current.x !== previous.x || current.y !== previous.y) adjusted.current = true;
    if (pointers.current.size === 1) {
      gesture.current.moved += Math.hypot(current.x - previous.x, current.y - previous.y);
      setView((view) => panBy(view, current.x - previous.x, current.y - previous.y));
    } else if (pointers.current.size === 2) {
      // Pinch: the other finger stays put for this event, this one moved from
      // `previous` to `current`. Zoom by the distance ratio around the old
      // midpoint, then pan by how far the midpoint travelled.
      const other = [...pointers.current.entries()].find(([id]) => id !== event.pointerId)?.[1];
      if (other) {
        const before = Math.hypot(previous.x - other.x, previous.y - other.y);
        const after = Math.hypot(current.x - other.x, current.y - other.y);
        const midBefore = { x: (previous.x + other.x) / 2, y: (previous.y + other.y) / 2 };
        const midAfter = { x: (current.x + other.x) / 2, y: (current.y + other.y) / 2 };
        gesture.current.moved +=
          Math.hypot(midAfter.x - midBefore.x, midAfter.y - midBefore.y) + Math.abs(after - before);
        setView((view) =>
          panBy(
            zoomAt(view, before > 0 ? after / before : 1, midBefore, limits.current),
            midAfter.x - midBefore.x,
            midAfter.y - midBefore.y,
          ),
        );
      }
    }
    if (gesture.current.moved > DRAG_THRESHOLD_PX) capture(event.currentTarget, event.pointerId);
    pointers.current.set(event.pointerId, current);
  }

  function onPointerUp(event: PointerEvent<HTMLDivElement>): void {
    pointers.current.delete(event.pointerId);
  }

  // A pointer that left without capture (press on a marker, then out) never
  // reports its pointerup here; a stale entry would turn the next single touch
  // into a phantom pinch.
  function onPointerLeave(event: PointerEvent<HTMLDivElement>): void {
    if (!event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      pointers.current.delete(event.pointerId);
    }
  }

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>): void {
    if (closestTo(event.target, OVERLAY_SELECTOR)) return;
    if (event.key === '+' || event.key === '=') zoomAtCentre(KEY_ZOOM_FACTOR);
    else if (event.key === '-') zoomAtCentre(1 / KEY_ZOOM_FACTOR);
    else if (event.key === '0') fit();
    else return;
    event.preventDefault();
  }

  return {
    view,
    fit,
    toLocal,
    wasDrag: () => gesture.current.moved > DRAG_THRESHOLD_PX,
    handlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp,
      onPointerCancel: onPointerUp,
      onPointerLeave,
      // The browser dropped the capture (e.g. a system gesture took over).
      onLostPointerCapture: onPointerUp,
      onKeyDown,
    },
  };
}
